/**
 * Sequence diagram for a reference docs page (`.doc`): who takes part in a process and in
 * what order they talk. One column per participant, one row per step, time runs downward.
 * It is laid out with CSS grid and real text, not an image: labels wrap, the colours are
 * the page's tokens in every skin and mode, and a screen reader gets an ordered list.
 * Static — the participants and steps come from the page's markdown (a `mermaid`
 * `sequenceDiagram` fence under a `docs:sequence` directive, see /docs-writer), never
 * from a query.
 */
import type { CSSProperties, ReactNode } from 'react';
import './DocSequence.css';

export interface DocSeqParticipant {
  id: string;
  label: string;
  /** `person` = someone who acts (drawn with a figure). Default `system`. */
  kind?: 'person' | 'system';
  /** One short line under the label (e.g. the role or where it runs). */
  note?: string;
}

export type DocSeqStep =
  /** A message. `reply` is drawn dashed. `from === to` is a step a participant does alone. */
  | { kind?: 'call' | 'reply'; from: string; to: string; text: ReactNode }
  /** A remark across one participant or a span of them. Not numbered. */
  | { kind: 'note'; over: [string] | [string, string]; text: ReactNode };

export interface DocSequenceProps {
  caption: string;
  participants: DocSeqParticipant[];
  steps: DocSeqStep[];
}

// The header row is repeated under a diagram longer than this, so the last steps can
// still be read against the participants without scrolling back up.
const REPEAT_HEAD_AFTER = 8;

function PersonGlyph() {
  return (
    <svg className="doc-seq-person" viewBox="0 0 12 12" aria-hidden="true">
      <circle cx="6" cy="3.2" r="2.2" />
      <path d="M1.6 11.2c0-2.6 2-4.2 4.4-4.2s4.4 1.6 4.4 4.2" />
    </svg>
  );
}

function Head({ participants }: { participants: DocSeqParticipant[] }) {
  return (
    <div className="doc-seq-row doc-seq-head" aria-hidden="true">
      {participants.map((p) => (
        <div key={p.id} className="doc-seq-who">
          <span className="doc-seq-who-label">
            {p.kind === 'person' && <PersonGlyph />}
            {p.label}
          </span>
          {p.note && <span className="doc-seq-who-note">{p.note}</span>}
        </div>
      ))}
    </div>
  );
}

export function DocSequence({ caption, participants, steps }: DocSequenceProps) {
  const n = participants.length;
  const index = new Map(participants.map((p, i) => [p.id, i + 1]));
  const col = (id: string) => {
    const i = index.get(id);
    if (!i) throw new Error(`DocSequence "${caption}": unknown participant "${id}"`);
    return i;
  };
  const name = (id: string) => participants[col(id) - 1]!.label;
  const people = participants.filter((p) => p.kind === 'person').length;
  const systems = n - people;
  const count =
    people && systems
      ? `${n} participants: ${people} ${people === 1 ? 'person' : 'people'}, ${systems} ${systems === 1 ? 'system' : 'systems'}`
      : `${n} participants`;

  let num = 0;
  const rows = steps.map((s, i) => {
    if (s.kind === 'note') {
      const a = col(s.over[0]);
      const b = col(s.over[1] ?? s.over[0]);
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      return (
        <li key={i} className="doc-seq-row">
          {/* A participant owns half-columns 2i-1 and 2i; its lifeline is grid line 2i. */}
          <div className="doc-seq-note" style={{ gridColumn: `${2 * lo - 1} / ${2 * hi + 1}` }}>
            <span className="doc-vh">Note: </span>
            {s.text}
          </div>
        </li>
      );
    }
    num += 1;
    const from = col(s.from);
    const to = col(s.to);
    if (from === to) {
      // Room for the box on the right of the lifeline, except for the last participant.
      const flip = from === n && n > 1;
      const gridColumn = flip ? `${Math.max(1, 2 * from - 3)} / ${2 * from}` : `${2 * from} / ${Math.min(2 * n + 1, 2 * from + 4)}`;
      return (
        <li key={i} className="doc-seq-row">
          <div className={`doc-seq-self${flip ? ' doc-seq-self--flip' : ''}`} style={{ gridColumn }}>
            <span className="doc-seq-n">{num}</span>
            <span>
              <span className="doc-vh">{name(s.from)}: </span>
              {s.text}
            </span>
          </div>
        </li>
      );
    }
    const dir = from < to ? 'right' : 'left';
    const reply = s.kind === 'reply';
    return (
      <li key={i} className="doc-seq-row">
        <div
          className={`doc-seq-msg doc-seq-msg--${dir}${reply ? ' doc-seq-msg--reply' : ''}`}
          style={{ gridColumn: `${2 * Math.min(from, to)} / ${2 * Math.max(from, to)}` }}
        >
          <span className="doc-seq-label">
            <span className="doc-seq-n">{num}</span>
            <span>
              <span className="doc-vh">
                {name(s.from)} {reply ? 'answers' : 'to'} {name(s.to)}:{' '}
              </span>
              {s.text}
            </span>
          </span>
          <span className="doc-seq-line" aria-hidden="true" />
        </div>
      </li>
    );
  });

  return (
    <figure className="doc-seq" style={{ '--seq-n': n } as CSSProperties}>
      <figcaption className="doc-seq-cap">
        <span>{caption}</span>
        <span className="doc-seq-count">{count}</span>
      </figcaption>
      <div className="doc-seq-scroll" tabIndex={0} role="group" aria-label={`${caption}. ${count}.`}>
        <div className="doc-seq-grid">
          <Head participants={participants} />
          <div className="doc-seq-body">
            <div className="doc-seq-lines" aria-hidden="true">
              {participants.map((p) => (
                <span key={p.id} />
              ))}
            </div>
            <ol className="doc-seq-steps">{rows}</ol>
          </div>
          {steps.length > REPEAT_HEAD_AFTER && <Head participants={participants} />}
        </div>
      </div>
    </figure>
  );
}
